/**
 * @fileoverview Field source classifier for the template library preview.
 *
 * Purpose:
 * - Determine where a placeholder's value will conceptually come from
 *   (job record, org preset, custom firm field, template extraction default,
 *   or manual per-document entry) so the preview can colour-code each chip.
 *
 * Used by:
 * - `PreviewDialog` in `_layout.settings.docs.tsx` — annotates sanitised
 *   `templateHtml` with `data-field-source` attributes consumed by the CSS
 *   in `src/index.css`.
 *
 * Note: this is a render-time decoration only. No data is persisted onto
 * the chips — the classifier re-runs whenever the preview is reopened, so
 * adding new presets / custom fields takes effect immediately.
 */

import { JOB_DERIVABLE_FIELD_KEYS } from "@cvx/jobs/precedents/fieldMapping";
import {
  isAnyFirmRefKey,
  parseContactCustomFieldKey,
  parseContactFieldKey,
  parseContactSourceFieldKey,
} from "@cvx/jobs/precedents/contactFields";

export type FieldSource =
  | "job"
  | "org"
  | "custom"
  | "template"
  | "manual"
  | "firm_contact"
  | "firm_reference"
  | "job_contact"
  | "job_contact_source";

/**
 * Shared label + chip-style classes per field source. Used by every UI
 * that wants to colour-code a placeholder by where its value comes from
 * (preview legend, field-insertion picker, fill-panel badges).
 */
export const FIELD_SOURCE_STYLES: Record<
  FieldSource,
  { label: string; chip: string; help: string }
> = {
  job: {
    label: "Job",
    chip: "bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-400 dark:border-emerald-900",
    help: "Populated from Job data",
  },
  org: {
    label: "Org",
    chip: "bg-blue-50 text-blue-700 border border-blue-200 dark:bg-blue-950/30 dark:text-blue-400 dark:border-blue-900",
    help: "Populated from Firm Org Fields",
  },
  custom: {
    label: "Custom",
    chip: "bg-violet-50 text-violet-700 border border-violet-200 dark:bg-violet-950/30 dark:text-violet-400 dark:border-violet-900",
    help: "Populated from Firm-level Custom Fields",
  },
  template: {
    label: "Unmapped",
    chip: "bg-cyan-50 text-cyan-700 border border-cyan-200 dark:bg-cyan-950/30 dark:text-cyan-400 dark:border-cyan-900",
    help: "Currently unmapped — suggest replacing with mapped field",
  },
  manual: {
    label: "Manual",
    chip: "bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-950/30 dark:text-amber-400 dark:border-amber-900",
    help: "Reflects empty Firm fields & requires manual input on generation",
  },
  job_contact: {
    label: "Job Contact",
    chip: "bg-rose-50 text-rose-700 border border-rose-200 dark:bg-rose-950/30 dark:text-rose-400 dark:border-rose-900",
    help: "Populated from linked Job Contact data",
  },
  firm_contact: {
    label: "Firm Contact",
    chip: "bg-orange-50 text-orange-700 border border-orange-200 dark:bg-orange-950/30 dark:text-orange-400 dark:border-orange-900",
    help: "Populated from linked Firm Contact data",
  },
  firm_reference: {
    label: "Firm Ref",
    chip: "bg-teal-50 text-teal-700 border border-teal-200 dark:bg-teal-950/30 dark:text-teal-400 dark:border-teal-900",
    help: "Populated from Firm Contact data",
  },
  job_contact_source: {
    label: "Job Contact Data",
    chip: "bg-pink-50 text-pink-700 border border-pink-200 dark:bg-pink-950/30 dark:text-pink-400 dark:border-pink-900",
    help: "Populated from data extracted from linked Job Contact",
  },
};

/** Ordered list of sources for use in filter tabs / legends. */
export const FIELD_SOURCE_ORDER: ReadonlyArray<FieldSource> = [
  "job",
  "org",
  "custom",
  "template",
  "manual",
  "job_contact",
  "job_contact_source",
  "firm_contact",
  "firm_reference",
];

/**
 * Classify a single field key into one of seven buckets.
 *
 * Precedence (highest first):
 *   `firm_contact / job_contact > custom > job > org > template > manual`.
 * - Contact fields win outright: the binding is encoded in the key itself
 *   (`contact:<domain>:…`), so the source is unambiguous and explicit.
 * - `custom` wins next because firms explicitly define these fields.
 * - `job` wins over `org` so a job-derived value never gets badged as a
 *   preset (e.g. `practitioner_name` is set from the job, even though it
 *   often also appears in a "practitioner" preset).
 * - `template` only kicks in for keys that aren't sourced any other way
 *   but have an `exampleValue` baked into the template's extracted fields.
 */
export function classifyFieldSource(args: {
  key: string;
  customFieldKeys: ReadonlySet<string>;
  orgPresetKeys: ReadonlySet<string>;
  templateFieldsWithExample: ReadonlySet<string>;
}): FieldSource {
  const { key, customFieldKeys, orgPresetKeys, templateFieldsWithExample } =
    args;

  // Collection item fields (`item_<part>`, used inside repeat sections)
  // resolve from agent-source data per repeated item — colour them like
  // the other source-linked contact data.
  if (key.startsWith("item_")) return "job_contact_source";

  // Source-linked contact fields (`contact_job_<source>_<part>`) must
  // be checked BEFORE the basic contact codec — both share the
  // `contact_<domain>_…` prefix, but only the source codec recognises
  // the `<source>_<part>` tail.
  const sourceBinding = parseContactSourceFieldKey(key);
  if (sourceBinding) return "job_contact_source";

  // Firm-contact references (`firmref_<category>_…`) carry a distinct
  // prefix, so they never collide with the `contact_*` codecs above —
  // classify them before the basic contact buckets.
  if (isAnyFirmRefKey(key)) return "firm_reference";

  // Custom contact fields (`contact_<domain>_cf_<key>`) share the same
  // prefix as basic contact keys but carry a `cf_` infix the basic parser
  // rejects — classify them with the basic contact buckets.
  const customBinding = parseContactCustomFieldKey(key);
  if (customBinding) {
    return customBinding.domain === "firm" ? "firm_contact" : "job_contact";
  }

  const contactBinding = parseContactFieldKey(key);
  if (contactBinding) {
    return contactBinding.domain === "firm" ? "firm_contact" : "job_contact";
  }
  if (customFieldKeys.has(key)) return "custom";
  if (JOB_DERIVABLE_FIELD_KEYS.has(key)) return "job";
  if (orgPresetKeys.has(key)) return "org";
  if (templateFieldsWithExample.has(key)) return "template";
  return "manual";
}

/**
 * Walk sanitised HTML, attach `data-field-source` to every
 * `[data-field-key]` element. Pure DOM operations, no React.
 *
 * Returns the original HTML unchanged on the server (no `DOMParser`) so
 * the caller can safely fall back to the un-annotated string during SSR.
 */
export function annotateFieldSources(
  html: string,
  classify: (key: string) => FieldSource,
): string {
  if (typeof DOMParser === "undefined") return html;

  const doc = new DOMParser().parseFromString(html, "text/html");
  const chips = doc.querySelectorAll<HTMLElement>("[data-field-key]");

  chips.forEach((chip) => {
    const key = chip.getAttribute("data-field-key");
    if (!key) return;
    chip.setAttribute("data-field-source", classify(key));
  });

  return doc.body.innerHTML;
}
